'use client';

import { ChevronRight } from 'lucide-react';

import { Link, type LinkProps } from '@natu/next-link';
import { cn } from '@natu/utils/cn';
import { usePageContext } from '@natu/utils/PageContextProvider';

import { env } from '../../../env';
import { Button, ButtonProps } from '../Button/Button';
import { ButtonWithEvent } from '../Button/ButtonWithEvent';

const colorVariants = {
  default: {
    buttonVariants: { linked: 'link-lightgray', notLinked: 'link-gray' },
    iconClassName: 'text-gray-400',
    span: 'text-gray-600',
    link: 'text-gray-400',
  },
  white: {
    buttonVariants: { linked: 'link-light', notLinked: 'link-white' },
    iconClassName: 'text-primary-200',
    span: 'text-white',
    link: 'text-primary-200',
  },
};

export type BreadcrumbColorVariant = keyof typeof colorVariants;

export type BreadcrumbItem = {
  title?: string;
  link?: LinkProps;
};

export interface BreadcrumbsProps {
  colorVariant?: BreadcrumbColorVariant;
  items?: BreadcrumbItem[];
  className?: string;
}

export const Breadcrumbs = ({
  items,
  colorVariant = 'default',
  className,
  ...rest
}: BreadcrumbsProps) => {
  const { buttonVariants, iconClassName, span, link: linkStyle } = colorVariants[colorVariant];
  const { isArticle } = usePageContext() || {};

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items?.map((breadcrumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: breadcrumb.title,
      item: breadcrumb.link?.href ? `${env.NEXT_PUBLIC_APP_URL}${breadcrumb.link.href}` : undefined,
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />

      <nav className={cn('max-w-full', className)} aria-label="breadcrumb" {...rest}>
        <ol className="flex items-center py-2">
          {items?.map((breadcrumb, index) => {
            const isNotLastItem = index < items.length - 1;
            const link = breadcrumb.link?.href;

            const buttonProps = {
              className: cn('whitespace-nowrap', !link && 'pointer-events-none'),
              size: 'link',
              variant: link ? buttonVariants.linked : buttonVariants.notLinked,
              asChild: true,
            } as ButtonProps;

            return (
              <li key={breadcrumb.title} className="flex items-center last:min-w-0">
                <meta itemProp="position" content={`${index + 1}`} />
                {isArticle ? (
                  <ButtonWithEvent event="E_click_blog_breadcrumb" {...buttonProps}>
                    {breadcrumb.link?.href ? (
                      <Link
                        className={cn('text-sm', linkStyle)}
                        {...breadcrumb.link}
                        itemProp="item"
                      >
                        <span className="font-medium" itemProp="name">
                          {breadcrumb.title}
                        </span>
                      </Link>
                    ) : (
                      <span className={cn('inline-block truncate text-sm', span)} itemProp="name">
                        {breadcrumb.title}
                      </span>
                    )}
                  </ButtonWithEvent>
                ) : (
                  <Button {...buttonProps}>
                    {breadcrumb.link?.href ? (
                      <Link
                        className={cn('text-sm', linkStyle)}
                        {...breadcrumb.link}
                        itemProp="item"
                      >
                        <span className="font-medium" itemProp="name">
                          {breadcrumb.title}
                        </span>
                      </Link>
                    ) : (
                      <span className={cn('inline-block truncate text-sm', span)} itemProp="name">
                        {breadcrumb.title}
                      </span>
                    )}
                  </Button>
                )}
                {isNotLastItem && (
                  <ChevronRight className={cn('mx-2 w-4 lg:mx-4', iconClassName)} />
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
};
